import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ButtonList, FeedCurveSettingsModal, FeedCurveViewer, Modal, PageHeader, SelectField } from '../../../Components';
import { colors, css, frames, images } from '../../../Styles';
import { FieldStyles, ButtonTypes, ListStyle_Types } from '../../../Definitions'
import { useAppSelector } from '../../../Store';
import Graph_QL, { useFeedComponents } from '../../../Services/Agrisys_GqlDataCaller';
import { FeedCurveInfo } from '../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedCurvesCall';
import { useFeedCurves } from '../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves';
import { FeedCurveTable } from './feedCurveTable';
import { getFeedCurveComponentsIds } from '../../../Components/_Components/getFeedCurveComponentsIds';
import { useMediaQuery } from '../../../Styles/mediaQueries';

// TODO: Translate

export default function FeedCurve() {
  const { t } = useTranslation();
  const location = useLocation();
  const [disableCurveNavigation, setDisableCurveNavigation] = useState(false);
  const [selectedFeedCurveId, setSelectedFeedCurveId] = useState<number>();
  const [showFeedCurve, setShowFeedCurve] = useState<FeedCurveInfo | undefined>(undefined);
  const [showFeedCurveModal, setShowFeedCurveModal] = useState<boolean>(false);
  const [showSpreadingModal, setShowSpreadingModal] = useState<boolean>(false);
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
  
  // Media Quiries
  const hideBtns = useMediaQuery(`(max-width: 1265px)`);
  const hideBtnText = useMediaQuery(`(max-width: 450px)`);
  const mobileXs = useMediaQuery(`(max-width: 690px)`);

  // Connection
  const farm = useAppSelector(({activeData}) => activeData.farm)
  const domain = useMemo(()=> (Graph_QL.ObjToString({domain: farm ?? ""})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm ?? ""})),[farm]);
  
  // #region | Feed Curves data
  const {call: getFeedCurvesInfo, data: feedCurvesState} = useFeedCurves(connection);
  
  const feedCurvesData = useMemo(() => {
    return feedCurvesState && feedCurvesState[domain] ? feedCurvesState[domain].data : [];
  },[domain, feedCurvesState])

  useEffect(() => {
    if (!feedCurvesState[domain]) {
      getFeedCurvesInfo(connection, {fetchAmount: 50})
    }
  },[connection, domain, feedCurvesState, getFeedCurvesInfo])

  useEffect(()=> {
    setShowFeedCurve(feedCurvesData.find((feedCurve) => feedCurve.id === selectedFeedCurveId))
  },[feedCurvesData, selectedFeedCurveId])

  // Get options from feedCurve Data and return the first to view if there isn't any selected from the feedCurves page.
  const feedCurvesOptions = useMemo(() => {
    const options = feedCurvesData.map(feedCurve => ({
      label: feedCurve.name,
      id: feedCurve.id
    }));
    if (!showFeedCurve && location.state?.params?.selected) {
      setSelectedFeedCurveId(location.state.params.selected);
    } else if (!showFeedCurve && options.length) {
      setSelectedFeedCurveId(options[0].id);
    }
    return options;
  }, [feedCurvesData, showFeedCurve, location.state.params.selected]);

  useEffect(() => {
    if (location.state?.params?.selected && !showFeedCurve && feedCurvesData) {
      setSelectedFeedCurveId(location.state.params.selected);
    }
  }, [feedCurvesData, location.state, selectedFeedCurveId, showFeedCurve]);
  // #endregion

  // #region | Feed Components data
  const {call: getFeedComponentsInfo, data: feedComponentsState} = useFeedComponents(connection);
 
  const feedCurveComponentIds = useMemo(() => {
    return getFeedCurveComponentsIds(showFeedCurve?.mixture)
  },[showFeedCurve]);

  const feedComponents = useMemo(() => {
    if (!showFeedCurve) {
      return []
    }
    const feedComponents = feedComponentsState && feedComponentsState[domain] ? feedComponentsState[domain].data : [];
    return feedComponents.filter((feedComponent) => feedCurveComponentIds.includes(feedComponent.id));
  },[domain, feedComponentsState, feedCurveComponentIds, showFeedCurve])

  useEffect(() => {
    if (showFeedCurve && (!feedComponentsState[domain] || feedComponents.length !== feedCurveComponentIds.length)) {
      getFeedComponentsInfo(connection, {ids: feedCurveComponentIds});
    }
  },[connection, domain, feedComponents, feedComponents.length, feedComponentsState, feedCurveComponentIds, getFeedComponentsInfo, showFeedCurve])
  // #endregion

  return (
    <frames.Page_inner>
      <frames._page_inner_header>
        <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between", zIndex: "999"}}>
          <div>
            <PageHeader topHeader={t("globalMenu.feedCurves")} />
            <SelectField
              isDisabled={disableCurveNavigation}
              options={feedCurvesOptions}
              defaultValue={showFeedCurve?.name}
              overrides={{
                fieldStyle: FieldStyles.greyDarkPrimary,
                textStart: true
              }}
              onChange={(e) => {
                setSelectedFeedCurveId(parseInt(String(e[0].id)));
              }}
            />
          </div>
            <ButtonList
              contents={[t("feed.feedCurve.spreading"), t("feed.feedCurve.corrections"), t("settings")]}
              onClick={(i => {
                if (i === 0) {setShowSpreadingModal(true)} // TODO CTO: Save to db and switch section edit
                else if (i === 1) {}
                else if (i === 2) {setShowSettingsModal(true)}
              })}
              buttonType={ButtonTypes.primaryButton}
              overrides={{
                baseButton: { ...( !hideBtns ? {...css.button_FitContent, minWidth: "none" } : {backgroundColor: colors.greyDarkSecondary})},
              }}
              listOverrides={{
                listStyle: !hideBtns ? ListStyle_Types.horizontal : ListStyle_Types.vertical,
              }}
              responsiveOverrides={{
                isResponsive: !hideBtns ? false : true,
                popoverStyle: { backgroundColor: colors.greyDarkPrimary, padding: "20px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px"+colors.blackPrimary+"4d" },
                openContent: hideBtnText ? "" : "Options"
              }}
              // const hideBtnText = useMediaQuery(`(max-width: 450px)`);
            />
        </div>
      </frames._page_inner_header>
      <frames._page_inner_content>
        {/* FeedCurveViewer */}
        <frames.FeedCurveViewerContainer>
          {showFeedCurve && <FeedCurveViewer data={showFeedCurve} feedComponents={feedComponents} showMilestoneLabelsBox={true} invertedColors={true}/>}
        </frames.FeedCurveViewerContainer>
        {/* FeedCurveTable */}
        {showFeedCurve && 
        <FeedCurveTable 
          connection={connection}
          setDisableCurveNavigation={setDisableCurveNavigation}
          showFeedCurve={showFeedCurve}
          feedComponents={feedComponents}
          feedCurveComponentIds={feedCurveComponentIds}
          watchFeedCurve={() => setShowFeedCurveModal(true)}
          />}
      </frames._page_inner_content>
      {/* Popup Modal for clicked feedcurve */}
      <Modal
        isModalOpen={!!showFeedCurveModal}
        overrides={{
          isPrimaryComponent: false,
          root: { padding: "40px 5px 5px 5px" },
          background: colors.greyLightTertiary
        }}
        modalHeader={`${showFeedCurve?.name}`}
        onCloseBtn={() => { setShowFeedCurveModal(false) }}
      >
        {/* Modal content */}
        <div style={{backgroundColor: colors.greyLightTertiary, borderRadius: "0px 0px 10px 10px", padding: "0px 20px 30px 20px"}}>
          {/* FeedCurveViewer */}
          {showFeedCurve && <FeedCurveViewer data={showFeedCurve} feedComponents={feedComponents} showComponentLabels={true} showMilestoneLabels={true}/>}
        </div>
      </Modal>
      <Modal 
        modalHeader={t("feed.feedCurve.spreading")}
        isModalOpen={!!showSpreadingModal}
        overrides={{
          isPrimaryComponent: false,
          root: { padding: "40px 5px 5px 5px" },
          background: colors.greyLightTertiary
        }}
        onCloseBtn={() => { setShowSpreadingModal(false) }}
      >
        <img src={images.images.spreading} style={{width: "1000px"}} alt={"Spreading"}/>
      </Modal>

      {showFeedCurve && 
        <FeedCurveSettingsModal closeModal={() => setShowSettingsModal(false)} isModalOpen={!!showSettingsModal} feedCurveNo={showFeedCurve.id} />
      }
    
    </frames.Page_inner>
  );
}